import * as React from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import UploadIcon from '@mui/icons-material/Upload';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import FileUploaded from '../components/FileUploaded';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useQuery } from '../hooks/useQuery';
import { useSnackbar } from '../hooks/useSnackbar';
import { isFileBig } from '../utils';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

export const ImportarPDFPage = ({
  activeStep,
  setActiveStep,
  steps,
  nfs,
  isContract,
  cnpj,
  contratoId,
  contratoDts,
}) => {
  const [file, setFile] = React.useState(null);
  let [searchParams, setSearchParams] = useSearchParams();
  const [isFetchingFiles, setIsFetchingFiles] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [filesUploaded, setFilesUploaded] = React.useState([]);
  const theme = useTheme();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const params = useParams();

  const {
    listarArquivosNotaFiscal,
    enviarArquivosNotaFiscal,
    deletarArquivoDaNotaFiscal,
  } = useQuery();
  const { id } = nfs;
  const inputFile = React.useRef(null);

  React.useEffect(() => {
    fetchFiles();
    setFile(null);
  }, [isFetchingFiles]);

  const fetchFiles = async () => {
    const response = await listarArquivosNotaFiscal(id, 'nota-fiscal');
    setFilesUploaded(response.data.data);
  };

  const sendFile = async () => {
    setIsUpdating(true);
    const formData = new FormData();
    formData.append('1', file);
    formData.append('qtn', 1);
    formData.append('tipo', 'nota-fiscal');
    await enviarArquivosNotaFiscal(id, formData);
    setIsFetchingFiles(!isFetchingFiles);
    setIsUpdating(false);
  };

  React.useEffect(() => {
    if (file) {
      sendFile();
    }
  }, [file]);

  const handleNext = async () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onPressDelete = async (fileUploaded, index) => {
    const confirm = await window.confirm('Deseja realmente excluir o arquivo?');

    if (confirm) {
      setIsUpdating(true);
      await deletarArquivoDaNotaFiscal(fileUploaded.id);
      setIsFetchingFiles(!isFetchingFiles);
      setIsUpdating(false);
    }
  };

  const onChangeFile = (event) => {
    const files = [...event.target.files];

    if (files.length > 0) {
      const file = files[0];
      const { type } = file;
      if (type !== 'application/pdf') {
        snackbar.showSnackbar('Tipo de arquivo inválido. Somente PDF');
        setFile(null);
        inputFile.current.value = '';
        return;
      }

      if (isFileBig(file.size)) {
        snackbar.showSnackbar('Tamanho de arquivo inválido. Máximo é 2MB');
        inputFile.current.value = '';
        return;
      }

      setFile(file);
    }
  };

  const canGoToNext = () => {
    return filesUploaded.length > 0;
  };

  return (
    <Card
      component="section"
      variant="outlined"
      style={{
        borderTop: `8px solid ${theme.palette.primary.light}`,
        marginTop: '2rem',
      }}
    >
      <CardContent>
        <Grid container spacing={2}>
          <Grid item sm={12}>
            <Typography
              component="h2"
              variant="h6"
              sx={{ color: '#0e72b8', marginTop: 0, fontWeight: 'bold' }}
            >
              Importar PDF da Nota Fiscal
            </Typography>
          </Grid>
          <Grid item sm={10}>
            <FormControl fullWidth>
              <TextField
                id="pdf-file-name"
                disabled
                style={{ width: '100%' }}
                value={(file && file.name) || ''}
                placeholder="Importar arquivo PDF"
                variant="outlined"
                aria-label="Nome do arquivo PDF selecionado"
              />
            </FormControl>
          </Grid>
          <Grid item sm={2}>
            <Button
              style={{ width: '100%', height: 40 }}
              variant="contained"
              component="label"
              disabled={filesUploaded.length === 1 || isUpdating}
              aria-label="Selecionar arquivo PDF para importar"
            >
              <UploadIcon />
              Importar
              <input
                onChange={onChangeFile}
                hidden
                accept="application/pdf"
                type="file"
                aria-label="Input de arquivo PDF"
              />
            </Button>
          </Grid>
        </Grid>

        {filesUploaded.map((item, index) => (
          <FileUploaded
            key={index}
            title={item.nome}
            link={item.link}
            disabled={item.can_delete ? isUpdating : true}
            onPressDelete={() => onPressDelete(item, index)}
          />
        ))}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            pt: 2,
            justifyContent: 'space-between',
          }}
        >
          <Button
            type="button"
            variant="contained"
            color="warning"
            style={{ marginRight: '0.5rem' }}
            onClick={() => {
              navigate(`/dashboard/notas/${params.contratoId}`);
              snackbar.showSnackbar('Rascunho salvo com sucesso.');
            }}
          >
            Salvar como rascunho
            <SaveIcon />
          </Button>

          <Button
            type="submit"
            onClick={handleNext}
            variant="contained"
            disabled={!canGoToNext()}
          >
            Próximo
            <ArrowRightAltIcon color={!canGoToNext() ? '#e0e0e0' : ''} />
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
